import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import LogActivityForm from "@/components/LogActivityForm";
import { fmtDate } from "@/lib/formatDate";
import { Search, SlidersHorizontal, Plus } from "lucide-react";

const TYPES = ["Intro Email","Follow-up Email","LinkedIn Request","LinkedIn Message","Call Attempt","Live Conversation","Voicemail","Meeting Scheduled","Submission Received","Market Overview Sent","Touching base","Referral / Intro"];
const CHANNELS = ["Email","Call","LinkedIn","Meeting","Referral","Other"];
const OUTCOMES = ["No Response","Awaiting Reply","Connected","Meeting Set","Submission Received","Nurture","Not a Fit","Active"];

export default function ActivityLog() {
    const [activities, setActivities] = useState([]);
    const [search, setSearch] = useState("");
    const [typeF, setTypeF] = useState("all");
    const [channelF, setChannelF] = useState("all");
    const [outcomeF, setOutcomeF] = useState("all");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [expanded, setExpanded] = useState({});
    const [logOpen, setLogOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const load = () => api.get("/activities").then((r) => setActivities(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    // Auto-open the log dialog when ?log=1 is in the URL (e.g. from Dashboard CTA)
    useEffect(() => {
        if (searchParams.get("log") === "1") {
            setLogOpen(true);
        }
    }, [searchParams]);

    const closeDialog = () => {
        setLogOpen(false);
        if (searchParams.get("log")) {
            const next = new URLSearchParams(searchParams);
            next.delete("log");
            setSearchParams(next, { replace: true });
        }
    };

    const filtered = useMemo(() => {
        return activities.filter((a) => {
            if (search && !(a.agency_name || "").toLowerCase().includes(search.toLowerCase())) return false;
            if (typeF !== "all" && a.activity_type !== typeF) return false;
            if (channelF !== "all" && a.channel !== channelF) return false;
            if (outcomeF !== "all" && a.outcome !== outcomeF) return false;
            const ad = (a.activity_date || "").slice(0, 10);
            if (from && ad < from) return false;
            if (to && ad > to) return false;
            return true;
        });
    }, [activities, search, typeF, channelF, outcomeF, from, to]);

    const fmt = (d) => fmtDate(d);

    return (
        <div className="p-6 lg:p-8 space-y-5 animate-fade-in" data-testid="activity-log-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <h1 className="ph-h1">Activity log</h1>
                    <p className="text-sm text-ph-muted mt-1">Reverse-chronological log of every broker touch.</p>
                </div>
                <Button
                    onClick={() => setLogOpen(true)}
                    className="bg-ph-primary text-white font-medium h-9 px-3 hover:bg-ph-primaryHover"
                    data-testid="open-log-activity-btn"
                >
                    <Plus className="w-4 h-4 mr-1.5" /> Log activity
                </Button>
            </div>

            <div className="ph-card p-4">
                <div className="flex items-center gap-2 mb-3">
                    <SlidersHorizontal className="w-3.5 h-3.5 text-ph-muted" />
                    <span className="text-xs font-medium text-ph-text">Filters</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                    <div className="relative md:col-span-2">
                        <Search className="absolute left-3 top-3 w-4 h-4 text-ph-mutedSoft" />
                        <Input placeholder="Search agency…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10" data-testid="activity-search-input" />
                    </div>
                    <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10" data-testid="activity-from-input" />
                    <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10" data-testid="activity-to-input" />
                    <Select value={typeF} onValueChange={setTypeF}>
                        <SelectTrigger className="h-10" data-testid="activity-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All types</SelectItem>
                            {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={channelF} onValueChange={setChannelF}>
                        <SelectTrigger className="h-10" data-testid="activity-channel-filter"><SelectValue placeholder="Channel" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All channels</SelectItem>
                            {CHANNELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={outcomeF} onValueChange={setOutcomeF}>
                        <SelectTrigger className="h-10" data-testid="activity-outcome-filter"><SelectValue placeholder="Outcome" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All outcomes</SelectItem>
                            {OUTCOMES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="ph-card overflow-hidden">
                <div className="max-h-[calc(100vh-320px)] overflow-auto scrollbar-thin">
                    <table className="w-full text-sm" data-testid="activity-table">
                        <thead className="bg-ph-canvas text-[11px] font-medium text-ph-muted sticky top-0 z-10 border-b border-ph-border">
                            <tr>
                                <th className="text-left px-4 py-2.5">Date</th>
                                <th className="text-left px-4 py-2.5">Agency</th>
                                <th className="text-left px-4 py-2.5">Producer</th>
                                <th className="text-left px-4 py-2.5">Type</th>
                                <th className="text-left px-4 py-2.5">Channel</th>
                                <th className="text-left px-4 py-2.5">Outcome</th>
                                <th className="text-left px-4 py-2.5">Next Step</th>
                                <th className="text-left px-4 py-2.5">Next Due</th>
                                <th className="text-left px-4 py-2.5">Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 && (
                                <tr><td colSpan={9} className="text-center py-12 text-ph-muted">
                                    No touches logged yet. Log your first activity to start tracking momentum.
                                </td></tr>
                            )}
                            {filtered.map((a) => (
                                <tr key={a.id} className="border-t border-ph-borderSoft hover:bg-ph-canvas" data-testid={`activity-row-${a.id}`}>
                                    <td className="px-4 py-3 whitespace-nowrap text-ph-muted">{fmt(a.activity_date)}</td>
                                    <td className="px-4 py-3 font-medium text-ph-text">{a.agency_name}</td>
                                    <td className="px-4 py-3 text-ph-muted">{a.producer_name}</td>
                                    <td className="px-4 py-3">
                                        <span className="inline-flex px-2 py-0.5 rounded-full bg-ph-primarySoft text-ph-primary text-[11px] font-medium">{a.activity_type}</span>
                                    </td>
                                    <td className="px-4 py-3 text-ph-muted">{a.channel}</td>
                                    <td className="px-4 py-3 text-ph-muted">{a.outcome}</td>
                                    <td className="px-4 py-3 text-xs text-ph-primary">{a.next_step}</td>
                                    <td className="px-4 py-3 text-ph-muted">{fmt(a.next_due_date)}</td>
                                    <td className="px-4 py-3 max-w-xs">
                                        {a.notes ? (
                                            <button
                                                onClick={() => setExpanded((p) => ({ ...p, [a.id]: !p[a.id] }))}
                                                className="text-left text-xs text-ph-muted hover:text-ph-text"
                                                data-testid={`expand-notes-${a.id}`}
                                            >
                                                {expanded[a.id] ? a.notes : (a.notes.length > 60 ? a.notes.slice(0, 60) + "…" : a.notes)}
                                            </button>
                                        ) : "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="px-4 py-2.5 text-xs text-ph-muted border-t border-ph-border bg-ph-canvas">
                    Showing <span className="font-medium text-ph-text">{filtered.length}</span> of {activities.length} touches
                </div>
            </div>

            <Dialog open={logOpen} onOpenChange={(o) => (o ? setLogOpen(true) : closeDialog())}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin" data-testid="log-activity-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-lg font-semibold text-ph-text">Log activity</DialogTitle>
                        <DialogDescription className="text-sm text-ph-muted">
                            A touch automatically updates the broker's last contact date and next follow-up.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="pt-2">
                        <LogActivityForm
                            onSuccess={() => {
                                closeDialog();
                                load();
                            }}
                            onCancel={closeDialog}
                        />
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
